import React, { useState, useEffect } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonList,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonChip,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
  IonFab,
  IonFabButton,
  IonProgressBar,
  IonLabel,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonRange,
  IonItem
} from '@ionic/react'
import { useLocation } from 'react-router-dom'
import { 
  addOutline,
  calendarOutline,
  journalOutline,
  trophyOutline,
  createOutline,
  analyticsOutline,
  flagOutline,
  heartOutline,
  sparklesOutline,
  documentTextOutline,
  statsChartOutline,
  arrowBackOutline,
  saveOutline,
  closeOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/PageHeader'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../services/supabase'
import { ReflectionEntry, Goal } from '../../types'
import PlatformService from '../../services/platform'

const GoalsPage: React.FC = () => {
  const { user, isCoachee } = useAuthStore()
  const { t } = useTranslation()
  const location = useLocation()
  const isWeb = PlatformService.isWeb()

  // Get initial tab from URL parameters
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    if (tab === 'goals' || tab === 'insights') return tab as 'reflections' | 'goals' | 'insights'
    return 'reflections'
  }

  // State management
  const [reflectionEntries, setReflectionEntries] = useState<ReflectionEntry[]>([])
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState<'reflections' | 'goals' | 'insights'>(getInitialTab())
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  // Modal states
  const [showReflectionModal, setShowReflectionModal] = useState(false)
  const [showGoalModal, setShowGoalModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reflection form state
  const [reflectionForm, setReflectionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    mood: 5,
    thoughts: '',
    insights: '',
    challenges: '',
    potential_solutions: '',
    tags: ['']
  })

  // Goal form state
  const [goalForm, setGoalForm] = useState({
    title: '',
    description: '',
    category: '',
    target_date: '',
    priority: 'medium' as 'low' | 'medium' | 'high'
  })

  // Mood emoji mapping
  const getMoodEmoji = (mood: number) => {
    if (mood <= 2) return '😢'
    if (mood <= 4) return '😟'
    if (mood <= 6) return '😐'
    if (mood <= 8) return '😊'
    return '😄'
  }

  const getMoodColor = (mood: number) => {
    if (mood <= 2) return 'danger'
    if (mood <= 4) return 'warning'
    if (mood <= 6) return 'medium'
    if (mood <= 8) return 'success'
    return 'primary'
  }

  // Reset reflection form
  const resetReflectionForm = () => {
    setReflectionForm({
      date: new Date().toISOString().split('T')[0],
      mood: 5,
      thoughts: '',
      insights: '',
      challenges: '',
      potential_solutions: '',
      tags: ['']
    })
  }

  // Reset goal form
  const resetGoalForm = () => {
    setGoalForm({
      title: '',
      description: '',
      category: '',
      target_date: '',
      priority: 'medium'
    })
  }

  // Handle reflection form submission
  const handleReflectionSubmit = async () => {
    if (!user || !reflectionForm.thoughts.trim()) {
      setToastMessage(t('reflectionJournal.messages.pleaseAddThoughts'))
      setShowToast(true)
      return
    }

    setIsSubmitting(true)
    try {
      const entryData = {
        coachee_id: user.id,
        date: reflectionForm.date,
        mood: reflectionForm.mood,
        thoughts: reflectionForm.thoughts.trim(),
        insights: reflectionForm.insights.trim() || null,
        challenges: reflectionForm.challenges.trim() || null,
        potential_solutions: reflectionForm.potential_solutions.trim() || null,
        tags: reflectionForm.tags.filter(tag => tag.trim()).length > 0 
          ? reflectionForm.tags.filter(tag => tag.trim()) 
          : null
      }

      const { error } = await supabase
        .from('reflection_entries')
        .insert([entryData])

      if (error) throw error

      setToastMessage(t('reflectionJournal.messages.reflectionAdded'))
      setShowToast(true)
      setShowReflectionModal(false)
      resetReflectionForm()
      fetchReflectionEntries()
    } catch (error) {
      console.error('Error adding reflection:', error)
      setToastMessage(t('reflectionJournal.messages.reflectionFailed'))
      setShowToast(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle goal form submission
  const handleGoalSubmit = async () => {
    if (!user || !goalForm.title.trim()) {
      setToastMessage(t('reflectionJournal.messages.pleaseEnterGoalTitle'))
      setShowToast(true)
      return
    }

    setIsSubmitting(true)
    try {
      const goalData = {
        user_id: user.id,
        title: goalForm.title.trim(),
        description: goalForm.description.trim() || null,
        category: goalForm.category.trim() || null,
        target_date: goalForm.target_date || null,
        priority: goalForm.priority,
        status: 'active',
        progress: 0
      }

      const { error } = await supabase
        .from('goals')
        .insert([goalData])

      if (error) throw error

      setToastMessage(t('reflectionJournal.messages.goalAdded'))
      setShowToast(true)
      setShowGoalModal(false)
      resetGoalForm()
      fetchGoals()
    } catch (error) {
      console.error('Error creating goal:', error)
      setToastMessage(t('reflectionJournal.messages.goalFailed'))
      setShowToast(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle array field changes for tags
  const handleTagChange = (index: number, value: string) => {
    const newTags = [...reflectionForm.tags]
    newTags[index] = value
    setReflectionForm(prev => ({ ...prev, tags: newTags }))
  }

  const addTag = () => {
    setReflectionForm(prev => ({ ...prev, tags: [...prev.tags, ''] }))
  }

  const removeTag = (index: number) => {
    const newTags = reflectionForm.tags.filter((_, i) => i !== index)
    setReflectionForm(prev => ({ ...prev, tags: newTags.length > 0 ? newTags : [''] }))
  }

  // Fetch reflection entries
  const fetchReflectionEntries = async () => {
    if (!user || !isCoachee()) return

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('reflection_entries')
        .select('*')
        .eq('coachee_id', user.id)
        .order('date', { ascending: false })

      if (error) throw error
      setReflectionEntries(data || [])
    } catch (error) {
      console.error('Error fetching reflection entries:', error)
      setToastMessage(t('reflectionJournal.messages.reflectionFailed'))
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  // Fetch goals
  const fetchGoals = async () => {
    if (!user || !isCoachee()) return

    try {
      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setGoals(data || [])
    } catch (error) {
      console.error('Error fetching goals:', error)
      setToastMessage(t('reflectionJournal.messages.goalFailed'))
      setShowToast(true)
    }
  }

  // Filter entries based on search
  const filteredEntries = reflectionEntries.filter(entry => {
    const searchLower = searchTerm.toLowerCase()
    return !searchTerm || 
      entry.thoughts?.toLowerCase().includes(searchLower) ||
      entry.insights?.toLowerCase().includes(searchLower) ||
      entry.challenges?.toLowerCase().includes(searchLower) ||
      entry.tags?.some(tag => tag.toLowerCase().includes(searchLower))
  })

  const filteredGoals = goals.filter(goal => {
    const searchLower = searchTerm.toLowerCase()
    return !searchTerm ||
      goal.title?.toLowerCase().includes(searchLower) ||
      goal.description?.toLowerCase().includes(searchLower) ||
      goal.category?.toLowerCase().includes(searchLower)
  })

  // Calculate mood statistics
  const moodStats = {
    average: reflectionEntries.length > 0 
      ? Math.round(reflectionEntries.reduce((sum, entry) => sum + (entry.mood || 5), 0) / reflectionEntries.length * 10) / 10
      : 0,
    trend: reflectionEntries.length >= 2 
      ? (reflectionEntries[0]?.mood || 5) - (reflectionEntries[1]?.mood || 5)
      : 0,
    totalEntries: reflectionEntries.length
  }

  // Calculate goal statistics
  const goalStats = {
    total: goals.length,
    completed: goals.filter(g => g.status === 'completed').length,
    active: goals.filter(g => g.status === 'active').length,
    overdue: goals.filter(g => g.target_date && new Date(g.target_date) < new Date() && g.status !== 'completed').length
  }

  useEffect(() => {
    if (user && isCoachee()) {
      fetchReflectionEntries()
      fetchGoals()
    }
  }, [user, isCoachee])

  // Redirect non-coachees
  if (!isCoachee()) {
    return (
      <IonPage>
        <PageHeader title={t('reflectionJournal.title')} />
        <IonContent className="ion-padding">
          <div className="text-center py-8">
            <p className="text-gray-500">{t('reflectionJournal.coacheeOnly')}</p>
          </div>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage>
      <PageHeader title={t('reflectionJournal.title')} />
      <IonContent>
        {/* Tab Navigation */}
        <div className="px-4 py-4">
          <IonSegment 
            value={activeTab} 
            onIonChange={(e) => setActiveTab(e.detail.value as any)}
          >
            <IonSegmentButton value="reflections">
              <IonIcon icon={journalOutline} />
              <IonLabel>{t('reflectionJournal.tabs.reflections')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="goals">
              <IonIcon icon={trophyOutline} />
              <IonLabel>{t('reflectionJournal.tabs.goals')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="insights">
              <IonIcon icon={analyticsOutline} />
              <IonLabel>{t('reflectionJournal.tabs.insights')}</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </div>

        {/* Search Bar */}
        <div className="px-4 mb-4">
          <IonSearchbar
            value={searchTerm}
            onIonInput={(e) => setSearchTerm(e.detail.value!)}
            placeholder={activeTab === 'goals' ? t('reflectionJournal.search.goalsPlaceholder') : t('reflectionJournal.search.reflectionsPlaceholder')}
            showClearButton="focus"
          />
        </div>

        {/* Content based on active tab */}
        <div className="px-4">
          {activeTab === 'reflections' && (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">{t('reflectionJournal.loading.reflections')}</p>
                </div>
              ) : filteredEntries.length > 0 ? (
                <IonList className="ion-no-padding">
                  {filteredEntries.map((entry) => (
                    <IonCard key={entry.id} className="mb-3">
                      <IonCardContent className="py-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center mb-2">
                              <div className="text-2xl mr-3">{getMoodEmoji(entry.mood || 5)}</div>
                              <div>
                                <h3 className="font-medium text-lg">
                                  {new Date(entry.date).toLocaleDateString()}
                                </h3>
                                <IonChip 
                                  color={getMoodColor(entry.mood || 5)}
                                  className="text-xs"
                                >
                                  {t('reflectionJournal.reflections.mood')}: {entry.mood || 5}/10
                                </IonChip>
                              </div>
                            </div>
                            
                            {entry.thoughts && (
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                <strong>{t('reflectionJournal.reflections.thoughts')}:</strong> {entry.thoughts}
                              </p>
                            )}
                            
                            {entry.insights && (
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                <strong>{t('reflectionJournal.reflections.insights')}:</strong> {entry.insights}
                              </p>
                            )}

                            {entry.tags && entry.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {entry.tags.slice(0, 3).map((tag, index) => (
                                  <IonChip key={index} className="text-xs" color="medium">
                                    {tag}
                                  </IonChip>
                                ))}
                                {entry.tags.length > 3 && (
                                  <IonChip className="text-xs" color="medium">
                                    +{entry.tags.length - 3} {t('reflectionJournal.reflections.moreLabel')}
                                  </IonChip>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col space-y-2 ml-3">
                            <IonButton 
                              fill="clear" 
                              size="small"
                            >
                              <IonIcon icon={createOutline} />
                            </IonButton>
                          </div>
                        </div>
                      </IonCardContent>
                    </IonCard>
                  ))}
                </IonList>
              ) : (
                <div className="text-center py-12">
                  <IonIcon icon={journalOutline} className="text-6xl text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm ? t('reflectionJournal.reflections.noMatchingReflections') : t('reflectionJournal.reflections.startJourney')}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {searchTerm 
                      ? t('reflectionJournal.reflections.tryDifferentSearch')
                      : t('reflectionJournal.reflections.documentExperiences')
                    }
                  </p>
                  {!searchTerm && isWeb && (
                    <IonButton 
                      expand="block" 
                      className="max-w-xs mx-auto hidden md:flex"
                      onClick={() => setShowReflectionModal(true)}
                    >
                      <IonIcon icon={addOutline} slot="start" />
                      {t('reflectionJournal.reflections.addFirstReflection')}
                    </IonButton>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'goals' && (
            <>
              {/* Goal Statistics Cards */}
              <IonGrid className="ion-no-padding mb-4">
                <IonRow>
                  <IonCol size="6" sizeMd="3">
                    <IonCard className="text-center">
                      <IonCardContent className="py-3">
                        <div className="text-2xl font-bold text-primary-600">{goalStats.total}</div>
                        <div className="text-xs text-gray-600">{t('reflectionJournal.goals.statistics.totalGoals')}</div>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                  <IonCol size="6" sizeMd="3">
                    <IonCard className="text-center">
                      <IonCardContent className="py-3">
                        <div className="text-2xl font-bold text-success-600">{goalStats.completed}</div>
                        <div className="text-xs text-gray-600">{t('reflectionJournal.goals.statistics.completed')}</div>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                  <IonCol size="6" sizeMd="3">
                    <IonCard className="text-center">
                      <IonCardContent className="py-3">
                        <div className="text-2xl font-bold text-warning-600">{goalStats.active}</div>
                        <div className="text-xs text-gray-600">{t('reflectionJournal.goals.statistics.active')}</div>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                  <IonCol size="6" sizeMd="3">
                    <IonCard className="text-center">
                      <IonCardContent className="py-3">
                        <div className="text-2xl font-bold text-danger-600">{goalStats.overdue}</div>
                        <div className="text-xs text-gray-600">{t('reflectionJournal.goals.statistics.overdue')}</div>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                </IonRow>
              </IonGrid>

              {filteredGoals.length > 0 ? (
                <IonList className="ion-no-padding">
                  {filteredGoals.map((goal) => (
                    <IonCard key={goal.id} className="mb-3">
                      <IonCardContent className="py-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center mb-2">
                              <h3 className="font-medium text-lg mr-2">{goal.title}</h3>
                              <IonChip 
                                color={
                                  goal.status === 'completed' ? 'success' :
                                  goal.status === 'paused' ? 'warning' : 'primary'
                                }
                                className="text-xs"
                              >
                                {goal.status}
                              </IonChip>
                            </div>
                            
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-gray-600">{t('reflectionJournal.goals.progress')}</span>
                                <span className="text-sm text-gray-600">{goal.progress || 0}%</span>
                              </div>
                              <IonProgressBar 
                                value={(goal.progress || 0) / 100} 
                                color={goal.progress === 100 ? 'success' : 'primary'}
                              />
                            </div>

                            {goal.description && (
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                {goal.description}
                              </p>
                            )}

                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              {goal.target_date && (
                                <div className="flex items-center">
                                  <IonIcon icon={calendarOutline} className="mr-1" />
                                  <span>{new Date(goal.target_date).toLocaleDateString()}</span>
                                </div>
                              )}
                              <div className="flex items-center">
                                <IonIcon icon={documentTextOutline} className="mr-1" />
                                <span className="capitalize">{goal.category}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col space-y-2 ml-3">
                            <IonButton 
                              fill="clear" 
                              size="small"
                            >
                              <IonIcon icon={createOutline} />
                            </IonButton>
                          </div>
                        </div>
                      </IonCardContent>
                    </IonCard>
                  ))}
                </IonList>
              ) : (
                <div className="text-center py-12">
                  <IonIcon icon={trophyOutline} className="text-6xl text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm ? t('sessions.goalsTab.noMatchingGoals') : t('reflectionJournal.goals.addFirstGoal')}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {searchTerm 
                      ? t('reflectionJournal.reflections.tryDifferentSearch')
                      : t('reflectionJournal.goals.noGoalsMessage')
                    }
                  </p>
                  {!searchTerm && isWeb && (
                    <IonButton 
                      expand="block" 
                      className="max-w-xs mx-auto hidden md:flex"
                      onClick={() => setShowGoalModal(true)}
                    >
                      <IonIcon icon={addOutline} slot="start" />
                      {t('reflectionJournal.goals.addFirstGoal')}
                    </IonButton>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'insights' && (
            <div className="space-y-4">
              {/* Mood Statistics Card */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="flex items-center text-base">
                    <IonIcon icon={heartOutline} className="mr-2" />
                    Mood Insights
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonGrid className="ion-no-padding">
                    <IonRow>
                      <IonCol size="4" className="text-center">
                        <div className="text-2xl">{getMoodEmoji(Math.round(moodStats.average))}</div>
                        <div className="text-lg font-bold">{moodStats.average}</div>
                        <div className="text-xs text-gray-600">Average Mood</div>
                      </IonCol>
                      <IonCol size="4" className="text-center">
                        <div className="text-2xl">
                          {moodStats.trend > 0 ? '📈' : moodStats.trend < 0 ? '📉' : '➡️'}
                        </div>
                        <div className="text-lg font-bold">
                          {moodStats.trend > 0 ? '+' : ''}{moodStats.trend.toFixed(1)}
                        </div>
                        <div className="text-xs text-gray-600">Recent Trend</div>
                      </IonCol>
                      <IonCol size="4" className="text-center">
                        <div className="text-2xl">📝</div>
                        <div className="text-lg font-bold">{moodStats.totalEntries}</div>
                        <div className="text-xs text-gray-600">Total Entries</div>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCardContent>
              </IonCard>

              {/* Common Themes */}
              {reflectionEntries.length > 0 && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base">
                      <IonIcon icon={sparklesOutline} className="mr-2" />
                      Common Themes
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="flex flex-wrap gap-2">
                      {Array.from(new Set(
                        reflectionEntries
                          .flatMap(entry => entry.tags || [])
                          .filter(tag => tag.trim())
                      )).slice(0, 10).map((tag, index) => (
                        <IonChip key={index} color="primary">
                          {tag}
                        </IonChip>
                      ))}
                    </div>
                  </IonCardContent>
                </IonCard>
              )}

              {/* Goals Progress Overview */}
              {goals.length > 0 && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base">
                      <IonIcon icon={statsChartOutline} className="mr-2" />
                      Goals Progress
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="space-y-3">
                      {goals.slice(0, 5).map((goal) => (
                        <div key={goal.id} className="flex items-center space-x-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{goal.title}</div>
                            <IonProgressBar 
                              value={(goal.progress || 0) / 100} 
                              color={goal.progress === 100 ? 'success' : 'primary'}
                              className="mt-1"
                            />
                          </div>
                          <div className="text-sm text-gray-600">
                            {goal.progress || 0}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </IonCardContent>
                </IonCard>
              )}

              {reflectionEntries.length === 0 && goals.length === 0 && (
                <div className="text-center py-12">
                  <IonIcon icon={analyticsOutline} className="text-6xl text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('reflectionJournal.insights.comingSoon')}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {t('reflectionJournal.insights.message')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floating Action Buttons */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton 
            onClick={() => {
              if (activeTab === 'reflections') {
                setShowReflectionModal(true)
              } else if (activeTab === 'goals') {
                setShowGoalModal(true)
              }
            }}
            disabled={activeTab === 'insights'}
          >
            <IonIcon icon={addOutline} />
          </IonFabButton>
          <IonFabButton 
            onClick={() => setShowReflectionModal(true)}
            disabled={activeTab === 'insights'}
            className={activeTab === 'reflections' ? '' : 'opacity-50'}
          >
            <IonIcon icon={journalOutline} />
          </IonFabButton>
          <IonFabButton 
            onClick={() => setShowGoalModal(true)}
            disabled={activeTab === 'insights'}
            className={activeTab === 'goals' ? '' : 'opacity-50'}
          >
            <IonIcon icon={trophyOutline} />
          </IonFabButton>
        </IonFab>

        {/* Reflection Entry Modal */}
        <IonModal isOpen={showReflectionModal} onDidDismiss={() => setShowReflectionModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowReflectionModal(false)}>
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{t('reflectionJournal.modal.reflection.title')}</IonTitle>
              <IonButtons slot="end">
                <IonButton 
                  onClick={handleReflectionSubmit}
                  disabled={isSubmitting || !reflectionForm.thoughts.trim()}
                >
                  <IonIcon icon={saveOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="space-y-4">
              {/* Date */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.reflection.date')}</IonLabel>
                <IonInput
                  type="date"
                  value={reflectionForm.date}
                  onIonInput={(e) => setReflectionForm(prev => ({ ...prev, date: e.detail.value! }))}
                />
              </IonItem>

              {/* Mood */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.reflection.mood')} ({reflectionForm.mood}/10)</IonLabel>
                <div className="w-full pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">😢</span>
                    <span className="text-2xl">{getMoodEmoji(reflectionForm.mood)}</span>
                    <span className="text-sm">😄</span>
                  </div>
                  <IonRange
                    min={1}
                    max={10}
                    step={1}
                    value={reflectionForm.mood}
                    onIonChange={(e) => setReflectionForm(prev => ({ ...prev, mood: e.detail.value as number }))}
                    color="primary"
                  />
                </div>
              </IonItem>

              {/* Thoughts */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.reflection.thoughts')} {t('reflectionJournal.modal.reflection.thoughtsRequired')}</IonLabel>
                <IonTextarea
                  placeholder={t('reflectionJournal.modal.reflection.thoughtsPlaceholder')}
                  value={reflectionForm.thoughts}
                  onIonInput={(e) => setReflectionForm(prev => ({ ...prev, thoughts: e.detail.value! }))}
                  rows={4}
                  maxlength={1000}
                />
              </IonItem>

              {/* Insights */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.reflection.insights')}</IonLabel>
                <IonTextarea
                  placeholder={t('reflectionJournal.modal.reflection.insightsPlaceholder')}
                  value={reflectionForm.insights}
                  onIonInput={(e) => setReflectionForm(prev => ({ ...prev, insights: e.detail.value! }))}
                  rows={3}
                  maxlength={500}
                />
              </IonItem>

              {/* Challenges */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.reflection.challenges')}</IonLabel>
                <IonTextarea
                  placeholder={t('reflectionJournal.modal.reflection.challengesPlaceholder')}
                  value={reflectionForm.challenges}
                  onIonInput={(e) => setReflectionForm(prev => ({ ...prev, challenges: e.detail.value! }))}
                  rows={3}
                  maxlength={500}
                />
              </IonItem>

              {/* Potential Solutions */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.reflection.potentialSolutions')}</IonLabel>
                <IonTextarea
                  placeholder={t('reflectionJournal.modal.reflection.solutionsPlaceholder')}
                  value={reflectionForm.potential_solutions}
                  onIonInput={(e) => setReflectionForm(prev => ({ ...prev, potential_solutions: e.detail.value! }))}
                  rows={3}
                  maxlength={500}
                />
              </IonItem>

              {/* Tags */}
              <div className="space-y-2">
                <IonLabel className="text-sm font-medium">{t('reflectionJournal.modal.reflection.tags')}</IonLabel>
                {reflectionForm.tags.map((tag, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <IonInput
                      placeholder={t('reflectionJournal.modal.reflection.tagPlaceholder')}
                      value={tag}
                      onIonInput={(e) => handleTagChange(index, e.detail.value!)}
                      className="flex-1"
                    />
                    {reflectionForm.tags.length > 1 && (
                      <IonButton
                        fill="clear"
                        size="small"
                        color="danger"
                        onClick={() => removeTag(index)}
                      >
                        <IonIcon icon={closeOutline} />
                      </IonButton>
                    )}
                  </div>
                ))}
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={addTag}
                  disabled={reflectionForm.tags.length >= 5}
                >
                  <IonIcon icon={addOutline} slot="start" />
                  {t('reflectionJournal.modal.reflection.addTag')}
                </IonButton>
              </div>
            </div>
          </IonContent>
        </IonModal>

        {/* Goal Creation Modal */}
        <IonModal isOpen={showGoalModal} onDidDismiss={() => setShowGoalModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowGoalModal(false)}>
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{t('reflectionJournal.modal.goal.title')}</IonTitle>
              <IonButtons slot="end">
                <IonButton 
                  onClick={handleGoalSubmit}
                  disabled={isSubmitting || !goalForm.title.trim()}
                >
                  <IonIcon icon={saveOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="space-y-4">
              {/* Title */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.goal.goalTitle')} *</IonLabel>
                <IonInput
                  placeholder={t('reflectionJournal.modal.goal.titlePlaceholder')}
                  value={goalForm.title}
                  onIonInput={(e) => setGoalForm(prev => ({ ...prev, title: e.detail.value! }))}
                  maxlength={100}
                />
              </IonItem>

              {/* Description */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.goal.description')}</IonLabel>
                <IonTextarea
                  placeholder={t('reflectionJournal.modal.goal.descriptionPlaceholder')}
                  value={goalForm.description}
                  onIonInput={(e) => setGoalForm(prev => ({ ...prev, description: e.detail.value! }))}
                  rows={4}
                  maxlength={500}
                />
              </IonItem>

              {/* Category */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.goal.category')}</IonLabel>
                <IonSelect
                  placeholder={t('reflectionJournal.modal.goal.categoryPlaceholder')}
                  value={goalForm.category}
                  onIonChange={(e) => setGoalForm(prev => ({ ...prev, category: e.detail.value }))}
                >
                  <IonSelectOption value="Personal Development">Personal Development</IonSelectOption>
                  <IonSelectOption value="Career">Career</IonSelectOption>
                  <IonSelectOption value="Health & Fitness">Health & Fitness</IonSelectOption>
                  <IonSelectOption value="Relationships">Relationships</IonSelectOption>
                  <IonSelectOption value="Financial">Financial</IonSelectOption>
                  <IonSelectOption value="Education">Education</IonSelectOption>
                  <IonSelectOption value="Creative">Creative</IonSelectOption>
                  <IonSelectOption value="Other">Other</IonSelectOption>
                </IonSelect>
              </IonItem>

              {/* Priority */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.goal.priority')}</IonLabel>
                <IonSelect
                  value={goalForm.priority}
                  onIonChange={(e) => setGoalForm(prev => ({ ...prev, priority: e.detail.value }))}
                >
                  <IonSelectOption value="low">{t('reflectionJournal.modal.goal.priorityLow')}</IonSelectOption>
                  <IonSelectOption value="medium">{t('reflectionJournal.modal.goal.priorityMedium')}</IonSelectOption>
                  <IonSelectOption value="high">{t('reflectionJournal.modal.goal.priorityHigh')}</IonSelectOption>
                </IonSelect>
              </IonItem>

              {/* Target Date */}
              <IonItem>
                <IonLabel position="stacked">{t('reflectionJournal.modal.goal.targetDate')}</IonLabel>
                <IonInput
                  type="date"
                  value={goalForm.target_date}
                  onIonInput={(e) => setGoalForm(prev => ({ ...prev, target_date: e.detail.value! }))}
                />
              </IonItem>
            </div>
          </IonContent>
        </IonModal>

        {/* Toast for notifications */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
        />
      </IonContent>
    </IonPage>
  )
}

export default GoalsPage 